import { createRouter, createWebHistory } from 'vue-router';
import type { RouteLocationNormalized, RouteLocationNormalizedLoaded } from 'vue-router';
import routes from '@router/routes';
import routesNames from '@router/routesNames';
import { useAppStore, useUserStore } from '@coreStores/index';

const router = createRouter({
    history: createWebHistory(),
    routes,
    strict: true
});

router.beforeEach(async (to, from, next) => {
    if (requiresType(to, from, next)) {
        return;
    }

    if (requiresExists(to, from, next)) {
        return;
    }

    // if (testerOnlyGuard(to, from, next)) {
    //     return;
    // }

    if (requiresNoAuthGuard(to, from, next)) {
        return;
    }

    if (requiresAuthGuard(to, from, next)) {
        return;
    }

    if (requiresCustomAction(to, from, next)) {
        return;
    }

    next();
});

/**
 * A must-have B.O. type validator.
 */
const requiresType = (to: RouteLocationNormalized, from: RouteLocationNormalizedLoaded, next: Function): boolean => {
    if (to.matched.some((record) => record.meta.requiresType)) {
        const appStore = useAppStore();
        const userStore = useUserStore();

        if (!appStore.getTypes().includes(to.params['type'] as string) || to.params['type'] == '404') {
            userStore.setPage('404');

            next({
                name: '404'
            });

            return true;
        }
    }

    return false;
};

/**
 * A must-have path exits validator.
 */
const requiresExists = (to: RouteLocationNormalized, from: RouteLocationNormalizedLoaded, next: Function): boolean => {
    if (!to.matched.length) {
        const appStore = useAppStore();
        const userStore = useUserStore();

        if (!appStore.getTypes().includes(to.params['type'] as string) || to.params['type'] == '404') {
            userStore.setPage('404');

            next({
                name: '404'
            });

            return true;
        }
    }

    return false;
};

/**
 * No login required validator.
 */
const requiresNoAuthGuard = (
    to: RouteLocationNormalized,
    from: RouteLocationNormalizedLoaded,
    next: Function
): boolean => {
    if (to.matched.some((record) => record.meta.requiresNoAuthGuard)) {
        const userStore = useUserStore();

        if (userStore.getLoggedIn()) {
            next({
                path: `${to.params['type']}/home`
            });

            return true;
        }
    }

    return false;
};

/**
 * A must-have login validator.
 */
const requiresAuthGuard = (
    to: RouteLocationNormalized,
    from: RouteLocationNormalizedLoaded,
    next: Function
): boolean => {
    const userStore = useUserStore();
    const currentType = userStore.getType();
    var isGuardNeeded = false;
    var isVisible = false;

    if (currentType) {
        to.matched.some((record) => {
            if (record.meta.requiresAuthGuard) {
                isGuardNeeded = true;
            }

            if (typeof record.meta.visibility === 'string') {
                if (record.meta.visibility == 'all') {
                    isVisible = true;
                }
            } else {
                if ((record.meta.visibility as Array<string>).includes(currentType)) {
                    isVisible = true;
                }
            }
        });

        if (isGuardNeeded) {
            if (!userStore.getLoggedIn() && !userStore.getToken()) {
                next({
                    path: `${to.params['type']}/login`
                });

                return true;
            }

            if (!isVisible) {
                next({
                    path: `${to.params['type']}/home`
                });

                return true;
            }
        }
    }

    return false;
};

/**
 * A custom validator.
 */
const requiresCustomAction = (
    to: RouteLocationNormalized,
    from: RouteLocationNormalizedLoaded,
    next: Function
): boolean => {
    let paths = ['404', '500', 'ChangePassword'];

    if (paths.includes(to.name as string)) {
        // const appStore = useAppStore();
        // appStore.toggleLoader(false);
    }

    return false;
};

const testerOnlyGuard = (to: RouteLocationNormalized, from: RouteLocationNormalizedLoaded, next: Function): boolean => {
    if (to.matched.some((record) => record.meta.requiresTest)) {
        const userStore = useUserStore();

        if (to.matched.some((record) => record.meta.requiresAuth)) {
            if (!userStore.getLoggedIn()) {
                next({
                    path: `${to.params['type']}/home`
                });
            }
        }

        if (!userStore.getTestMode()) {
            next({
                path: `${to.params['type']}/home`
            });
        } else {
            next();
        }

        return true;
    }

    return false;
};

export default router;
